import { useCallback, useEffect, useMemo, useState } from "react";

import { useFetchMetaPickerProductsQuery } from "../../api/marketingApi";
import { buildPickerParams } from "./metaAdsModel";

export const useDebouncedValue = (value, delay = 250) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(timer);
  }, [value, delay]);
  return debounced;
};

/**
 * Pages of the product picker for the current filters, accumulated for
 * "load more" / infinite scroll.
 *
 * The page cursor is tied to the filter key, so changing a filter starts again
 * at page 1 in the same render — a stale "page 3" is never requested for new
 * filters, and pages from different filters are never mixed.
 */
const usePickerProducts = (filters, sort, { skip = false } = {}) => {
  const filterKey = JSON.stringify(buildPickerParams(filters, sort, 1));
  const [cursor, setCursor] = useState({ key: filterKey, page: 1 });
  // Derived-state reset during render: returning to earlier filters must start
  // from page 1 again, not resume the page they were scrolled to.
  if (cursor.key !== filterKey) setCursor({ key: filterKey, page: 1 });
  const page = cursor.key === filterKey ? cursor.page : 1;

  const { currentData, isFetching, isError, error, refetch } = useFetchMetaPickerProductsQuery(
    buildPickerParams(filters, sort, page),
    { skip }
  );

  const [loaded, setLoaded] = useState({ key: filterKey, pages: {}, meta: null });

  useEffect(() => {
    if (!currentData) return;
    setLoaded((previous) => {
      const sameKey = previous.key === filterKey;
      return {
        key: filterKey,
        pages: { ...(sameKey ? previous.pages : {}), [currentData.page]: currentData.items || [] },
        meta: currentData,
      };
    });
  }, [currentData, filterKey]);

  // Facets describe the whole catalog, so keep the last ones while new
  // filters load instead of emptying the dropdowns.
  const [facets, setFacets] = useState(null);
  useEffect(() => {
    if (currentData?.facets) setFacets(currentData.facets);
  }, [currentData]);

  // While new filters load, the previous results stay on screen (dimmed)
  // rather than flashing skeletons on every keystroke.
  const isStale = loaded.key !== filterKey;
  const items = useMemo(() => {
    const seen = new Set();
    return Object.keys(loaded.pages)
      .map(Number)
      .sort((a, b) => a - b)
      .flatMap((number) => loaded.pages[number])
      .filter((item) => (seen.has(item.key) ? false : seen.add(item.key)));
  }, [loaded.pages]);

  const meta = loaded.meta;
  const hasMore = Boolean(meta) && !isStale && page < (meta.totalPages || 1);

  const loadMore = useCallback(() => {
    if (isFetching || !hasMore) return;
    setCursor({ key: filterKey, page: page + 1 });
  }, [filterKey, hasMore, isFetching, page]);

  return {
    items,
    total: meta?.total ?? 0,
    // Only trustworthy for the filters on screen; empty while they load.
    matchingKeys: isStale ? [] : meta?.matchingKeys || [],
    facets,
    hasMore,
    loadMore,
    isInitialLoading: !meta && !isError,
    isRefreshing: Boolean(meta) && (isStale || (isFetching && page === 1)),
    isFetchingMore: !isStale && isFetching && page > 1,
    isError,
    error,
    refetch,
  };
};

export default usePickerProducts;
